class CartDrawer extends HTMLElement {
  constructor() {
    super()

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close())
    // Use event delegation so the listener survives DOM replacement
    this.addEventListener('click', (evt) => {
      if (evt.target.id === 'CartDrawer-Overlay') this.close()
    })
    this.setHeaderCartIconAccessibility()
  }

  connectedCallback() {
    this.getCart()
  }

  enforceCartRules() {
    if (this._enforcingRules) return
    this._enforcingRules = true

    const parseIds = (attr) =>
      attr ? attr.split(',').map((id) => id.trim()).filter(Boolean) : []

    const sampleVariantIds = parseIds(this.dataset.freeSampleVariantIds)
    const gwpVariantIds = parseIds(this.dataset.gwpVariantIds)
    const maxSamples = parseInt(this.dataset.maxSamples, 10) || 0
    const gwpThreshold = parseFloat(this.dataset.gwpThreshold) || 0
    const firstTimeGwpId = this.dataset.giftVariantId || ''
    const firstTimeGwpThreshold = parseFloat(this.dataset.giftThreshold) || 0
    const allFreeIds = [...new Set([...sampleVariantIds, ...gwpVariantIds, ...(firstTimeGwpId ? [firstTimeGwpId] : [])])]

    if (allFreeIds.length === 0) {
      this._enforcingRules = false
      return
    }

    fetch(`${routes.cart_url}.js`, {
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
    })
      .then((r) => r.json())
      .then((cart) => {
        const sampleItems = cart.items.filter((item) =>
          sampleVariantIds.includes(String(item.variant_id))
        )
        const gwpItems = cart.items.filter((item) =>
          gwpVariantIds.includes(String(item.variant_id)) && String(item.variant_id) !== firstTimeGwpId
        )
        const firstTimeGwpItem = firstTimeGwpId
          ? cart.items.find((item) => String(item.variant_id) === firstTimeGwpId)
          : null
        const totalSampleQty = sampleItems.reduce((sum, item) => sum + item.quantity, 0)

        const paidSubtotal = cart.items.reduce((sum, item) => {
          if (allFreeIds.includes(String(item.variant_id))) return sum
          return sum + item.final_line_price
        }, 0)
        const paidSubtotalDollars = paidSubtotal / 100
        const hasPaidItem = paidSubtotal > 0

        const updates = {}

        if (!hasPaidItem) {
          sampleItems.forEach((item) => { updates[item.key] = 0 })
          gwpItems.forEach((item) => { updates[item.key] = 0 })
          if (firstTimeGwpItem) updates[firstTimeGwpItem.key] = 0
        } else {
          if (totalSampleQty > maxSamples && maxSamples > 0) {
            let allowed = maxSamples
            sampleItems.forEach((item) => {
              if (allowed <= 0) {
                updates[item.key] = 0
              } else if (item.quantity > 1) {
                updates[item.key] = 1
                allowed -= 1
              } else {
                allowed -= item.quantity
              }
            })
          }

          if (paidSubtotalDollars < gwpThreshold) {
            gwpItems.forEach((item) => { updates[item.key] = 0 })
          } else {
            gwpItems.forEach((item) => {
              if (item.quantity > 1) updates[item.key] = 1
            })
          }

          if (firstTimeGwpItem) {
            if (paidSubtotalDollars < firstTimeGwpThreshold) {
              updates[firstTimeGwpItem.key] = 0
            } else if (firstTimeGwpItem.quantity > 1) {
              updates[firstTimeGwpItem.key] = 1
            }
          }
        }

        if (Object.keys(updates).length === 0) return

        return fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates,
            sections: this.getSectionsToRender().map((s) => s.id),
          }),
        })
          .then((r) => r.json())
          .then((state) => {
            if (state.sections) this._updateSections(state.sections)
            const isEmpty = state.item_count === 0
            this.classList.toggle('is-empty', isEmpty)
          })
      })
      .catch((err) => console.error('Error enforcing cart rules:', err))
      .finally(() => {
        this._enforcingRules = false
      })
  }

  getCart() {
    fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble&t=${Date.now()}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then((sections) => {
        this._updateSections(sections)
        this._updateSiteWideSalePrices(sections['cart-drawer'])

        const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html')
        const isEmpty = html.querySelector('.drawer__inner-empty') !== null
        this.classList.toggle('is-empty', isEmpty)

        this.enforceCartRules()
      })
      .catch((error) => {
        console.error('Error fetching latest cart:', error)
      })
  }

  _updateSections(sections) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id)
      if (sectionElement && sections[section.id]) {
        sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector)
      }
    })
    this.initializeFreeSampleSlider()
  }

  _updateSiteWideSalePrices(cartDrawerHtml) {
    const html = new DOMParser().parseFromString(cartDrawerHtml, 'text/html')
    const cartTotalElement = html.querySelector('[data-total-price]')
    if (!cartTotalElement) return

    const finalPrice = parseInt(cartTotalElement.dataset.totalPrice, 10) || 0
    const siteWideSaleTierOneDiscount = parseInt(this.dataset.siteWideSaleTierOneDiscount, 10) || 0
    const siteWideSaleTierTwoThreshold = parseInt(this.dataset.siteWideSaleTierTwoThreshold, 10) || 0

    if (siteWideSaleTierOneDiscount <= 0) return

    document.querySelectorAll('[data-site-wide-sale-tier-one]').forEach(($siteWideSaleTierOne) => {
      const $priceWrapper = $siteWideSaleTierOne.closest('[id^="price-"]')
      if (!$priceWrapper) return
      const $siteWideSaleTierTwo = $priceWrapper.querySelector('[data-site-wide-sale-tier-two]')
      if (!$siteWideSaleTierTwo) return

      const isASiteWideDiscountTierPriceVisible =
        $siteWideSaleTierOne.style.display !== 'none' || $siteWideSaleTierTwo.style.display !== 'none'

      if (!isASiteWideDiscountTierPriceVisible) return

      if (finalPrice >= siteWideSaleTierTwoThreshold) {
        $siteWideSaleTierOne.style.display = 'none'
        $siteWideSaleTierOne.removeAttribute('selected')
        $siteWideSaleTierTwo.style.display = 'inline-block'
        $siteWideSaleTierTwo.setAttribute('selected', true)
      } else {
        $siteWideSaleTierTwo.style.display = 'none'
        $siteWideSaleTierTwo.removeAttribute('selected')
        $siteWideSaleTierOne.style.display = 'inline-block'
        $siteWideSaleTierOne.setAttribute('selected', true)
      }
    })
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble')
    cartLink.setAttribute('role', 'button')
    cartLink.setAttribute('aria-haspopup', 'dialog')
    cartLink.addEventListener('click', (event) => {
      event.preventDefault()
      this.open(cartLink)
    })
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault()
        this.open(cartLink)
      }
    })
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy)
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary')
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote)
    const overlay = this.querySelector('#CartDrawer-Overlay')
    const splideSamples = this.querySelector('.splide-free-samples')
    if (splideSamples) {
      splideSamples.style.display = 'block'
    }
    setTimeout(() => {
      this.classList.add('animate', 'active')
      if (overlay) {
        overlay.classList.remove('opacity-0')
        overlay.classList.add('opacity-100')
      }
    })

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer')
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close')
        trapFocus(containerToTrapFocusOn, focusElement)

        // Initialize Splide slider on first open
        this.initializeFreeSampleSlider()
      },
      { once: true }
    )

    document.body.classList.add('overflow-hidden')
  }

  close() {
    const overlay = this.querySelector('#CartDrawer-Overlay')
    const splideSamples = this.querySelector('.splide-free-samples')
    if (splideSamples) {
      splideSamples.style.display = 'none'
    }

    if (overlay) {
      overlay.classList.remove('opacity-100')
      overlay.classList.add('opacity-0')
    }

    this.classList.remove('active')
    removeTrapFocus(this.activeElement)
    document.body.classList.remove('overflow-hidden')
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button')
    cartDrawerNote.setAttribute('aria-expanded', 'false')

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id)
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'))
    })

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape)
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty')
    this.productId = parsedState.id

    this._updateSections(parsedState.sections)
    this.enforceCartRules()

    setTimeout(() => {
      this.open()
    })
  }

  initializeFreeSampleSlider() {
    const freeSampleSplide = this.querySelector('.splide.splide-free-samples')
    if (freeSampleSplide && typeof Splide !== 'undefined') {
      // Destroy existing instance if it exists
      if (freeSampleSplide.splide) {
        freeSampleSplide.splide.destroy(true)
      }

      const slider = new Splide(freeSampleSplide, {
        type: 'slide',
        perPage: 3,
        perMove: 1,
        gap: '1rem',
        pagination: false,
        arrows: false,
      }).mount()

      if (typeof SplideHorizontalScroll !== 'undefined') {
        SplideHorizontalScroll(slider)
      }
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ]
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
  }

  setActiveElement(element) {
    this.activeElement = element
  }
}

customElements.define('cart-drawer', CartDrawer)

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ]
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble&t=${Date.now()}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then((parsedState) => {
        const $cartDrawer = document.querySelector('cart-drawer')

        if ($cartDrawer._updateSiteWideSalePrices) {
          $cartDrawer._updateSiteWideSalePrices(parsedState['cart-drawer'])
        }

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id)

          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector)
        })

        if ($cartDrawer.initializeFreeSampleSlider) {
          $cartDrawer.initializeFreeSampleSlider()
        }

        if ($cartDrawer.enforceCartRules) {
          $cartDrawer.enforceCartRules()
        }
      })
      .catch((e) => {
        console.error(e)
      })
  }
}

customElements.define('cart-drawer-items', CartDrawerItems)
